import {CELL_PHONE, COMPANY_NAME, COUNTRY_CODE, FLEET_NAME, RFH_FORM_ONCHANGE} from "../constants/form";
import {useTranslation} from "react-i18next";
import CustomHeader from "../components/shared/CustomHeader";
import {Controller, useForm} from "react-hook-form";
import CustomInput from "../components/shared/CustomInput";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import useYupSchema from "../hooks/useYupSchema";
import {getPersonalInfo, setPersonalInfo} from "../redux/slices/PersonalAccountInformationSlice";
import {useTrackSFOSteps} from "../react-query/Mutations";
import {getSubdomain} from "../config/StylesheetInjector";
import {useNavigate} from "react-router";
import {yupResolver} from "@hookform/resolvers/yup";
import {useMutation} from "@tanstack/react-query";
import {skipOnboarding} from "../services/OnboardingSteps";
import {notifyToaster, scrollToTop} from "../helpers/common";
import {updateUserAccount} from "../services/Address";
import {useNavigationHandler} from "../contexts/Navigation";
import {routerPaths} from "../utils/routerPaths";
import {gaJSON, useTrackFirebaseAnalytics} from "../utils/googleAnalytics";
import {trackEvent} from "../utils/segmentAnalytics";

const FleetAccountInfo = () => {
    const {t} = useTranslation("home");
    const dispatch = useDispatch();
    const {buildPersonalInfoSchema} = useYupSchema();
    const personalInfo = useSelector((state) => getPersonalInfo(state));
    const {mutateAsync: trackSFOSteps} = useTrackSFOSteps();
    const {registerCallbackFunction, handleContinueButtonLoading, handleContinueDisabled, setDisabledTooltip} = useNavigationHandler();

    const [isExpanded, setIsExpanded] = useState(true);

    const track = useTrackFirebaseAnalytics();
    useEffect(() => {
        if (personalInfo.id) {
            track(gaJSON.onboarding_screen_viewed, {step_name: "account-info"})
        }
    }, []);

    const userForm = {
        firstName: {
            label: t("userFormFirstNameLabel"),
            placeholder: t("userFormFirstNamePlaceholder"),
            required: true,
            type: "text",
            errorLabel: t("firstNameErrorLabel"),
            isEditable: true,
            inputHeader: t("userFormFirstNamePlaceholder"),
            isBackgroundWhite: true
        },
        lastName: {
            label: t("userFormLastNameLabel"),
            placeholder: t("userFormLastNamePlaceholder"),
            required: true,
            type: "text",
            errorLabel: t("lastNameErrorLabel"),
            isEditable: true,
            inputHeader: t("userFormLastNamePlaceholder"),
            isBackgroundWhite: true
        },
        [CELL_PHONE]: {
            label: t("userFormPhoneLabel"),
            placeholder: t("userFormPhonePlaceholder"),
            required: true,
            type: "phone",
            errorLabel: t("phoneErrorLabel"),
            isEditable: true,
            inputHeader: t("mobilePhoneNumber"),
            isBackgroundWhite: true
        },
        [COMPANY_NAME]: {
            label: t("userFormCompanyNameLabel"),
            placeholder: t("userFormCompanyNamePlaceholder"),
            required: true,
            type: "text",
            errorLabel: t("companyNameErrorLabel"),
            isEditable: false,
            wantClearIcon: false,
            inputHeader: t("userFormCompanyNamePlaceholder"),
        },
        email: {
            label: t("userFormEmailLabel"),
            placeholder: t("userFormEmailPlaceholder"),
            required: true,
            type: "email",
            errorLabel: t("emailErrorLabel"),
            isEditable: false,
            wantClearIcon: false,
            inputHeader: t("companyEmail"),
        },
    };

    const userSchema = buildPersonalInfoSchema(userForm);

    const sub = getSubdomain();

    const navigate = useNavigate();

    const {
        handleSubmit,
        control,
        formState: {errors},
        getValues,
        setValue,
        trigger,
        reset,
    } = useForm({
        resolver: yupResolver(userSchema),
        mode: RFH_FORM_ONCHANGE,
    });

    // Initialize form with personalInfo data
    useEffect(() => {
        if (personalInfo) {
            const initialValues = {
                firstName: personalInfo.firstName || '',
                lastName: personalInfo.lastName || '',
                [CELL_PHONE]: personalInfo.cellPhone ? formatPhoneNumber(personalInfo.cellPhone) : '',
                [COMPANY_NAME]: personalInfo[FLEET_NAME] || personalInfo.companyName || '',
                email: personalInfo.email || '',
            };
            reset(initialValues);
        }
    }, [personalInfo, reset]);

    useEffect(() => {
        if (Object.keys(errors).length !== 0) {
            handleContinueDisabled(true);
        } else {
            handleContinueDisabled(false);
        }
        setDisabledTooltip(null)
    }, [JSON.stringify(errors)]);

    const {
        mutateAsync: skipOnboardingMutation,
        isPending: skipOnboardingLoadere,
    } = useMutation({
        mutationFn: () => {
            return skipOnboarding();
        },
    });

    useEffect(() => {
        if (Object.keys(errors)?.length > 0) {
            scrollToTop();
        }
    }, [errors]);
    useEffect(() => {
        registerCallbackFunction(() => () => onSubmit(getValues()));
    }, []);

    const {mutateAsync: updateUserInfo, isPending} = useMutation({
        mutationFn: ({formData}) => {
            return updateUserAccount({
                customerId: personalInfo?.id,
                apiPayload: {
                    customer: {
                        firstName: formData?.firstName,
                        lastName: formData?.lastName,
                        fleet_name: formData?.companyName,
                        cellPhone: stripPhoneFormatting(formData?.cellPhone),
                        email: formData?.email,
                    },
                },
            });
        },
        onSuccess: (data) => {
            dispatch(setPersonalInfo({personalInfo: data?.result}));
        },
    });

    const onSubmit = async (data) => {
        try {
            handleContinueButtonLoading(true)
            await updateUserInfo({formData: data});
            trackEvent("Onboarding Fleet Info", {
                ...data,
                nextStep: 'enroll-vehicles'
            })
            scrollToTop();
            navigate(`${routerPaths.enrollVehicles}`);
        } catch (error) {
        } finally {
            handleContinueButtonLoading(false)
        }
    };

    const handleClear = (field) => {
        setValue(field, field === CELL_PHONE ? "" : "");
        trigger(field);
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        const cleaned = ('' + phone).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
        return phone;
    }

    function stripPhoneFormatting(phone) {
        return phone.replace(/\D/g, '');
    }

    return (
        <div className="home fleet-account">
            <div className="fleet-account-info">
                <div className="home-title-margin">
                    <CustomHeader className="title-gray" title={t("titleMain")}/>
                </div>
                <div className="home_userform__title">
                    <CustomHeader className="" title={t("titleUserForm")}/>
                </div>
                <form
                    className={`form__animation ${isExpanded ? "visible" : "hidden"}`}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    {Object.entries(userForm)?.map(([formField, fieldData]) => (
                        <div className="home__user-form" key={formField}>
                            <Controller
                                name={formField}
                                control={control}
                                defaultValue={
                                    personalInfo[
                                        formField === COMPANY_NAME ? FLEET_NAME : formField
                                        ] || ''
                                }
                                render={({field}) => (
                                    <CustomInput
                                        {...field}
                                        onChange={(e) => {
                                            if (formField === CELL_PHONE) {
                                                let value = e.target.value;
                                                field.onChange(formatPhoneNumber(value));
                                            } else if (fieldData.isEditable) {
                                                field.onChange(e);
                                            }
                                        }}
                                        value={formField === CELL_PHONE ? formatPhoneNumber(field.value) : field.value}
                                        placeholder={fieldData.placeholder}
                                        error={errors[formField]?.message}
                                        wantClearIcon={fieldData.wantClearIcon}
                                        inputHeader={fieldData.inputHeader}
                                        type={fieldData.type}
                                        isEditable={fieldData.isEditable}
                                        onClear={() =>
                                            fieldData.isEditable && handleClear(formField)
                                        }
                                    />
                                )}
                            />
                        </div>
                    ))}
                    <span className="text-gray">
                    {t('fleetInfoNote')}
                        </span>
                </form>
            </div>
        </div>
    );
};

export default FleetAccountInfo;